import { useState, useMemo, useEffect } from 'react';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { PROVIDER_MODAL_STEP, PROVIDER_MODE } from 'pages/Providers/Providers';
import { selectCurrentBusiness } from 'components/Providers/ProviderList';
import { connectStoreProvider, getProvidersStatuses } from 'redux/actions/providers';

export const initialDataModel = {
  storeId: '',
  previewUrl: '',
  username: '',
  password: '',
  generalAccountEmail: '',
};

const formSchema = yup.object().shape({
  storeId: yup.string().required('This field is required'),
  previewUrl: yup.string().url('Invalid URL').required('This field is required'),
  username: yup.string().required('This field is required'),
  password: yup.string().required('This field is required'),
});

const formSchemaUber = yup.object().shape({
  generalAccountEmail: yup.string().email('Email is not valid').nullable(),
});

const useProviderConnectReadOnly = (
  mode, 
  handleClose,
  providerSRV,
  providers,
  setProviderReadOnlyStep, 
  handleContinue, 
  filteredListWithGeneralAccountEmail,
  open
) => {
  const dispatch = useDispatch();
  const currentBusiness = useSelector(selectCurrentBusiness);
  const [loading, setLoading] = useState(false);

  const isUber = useMemo(() => {
    return providers?.ubereats?.srv === providerSRV;
  }, [providers, providerSRV]);

  const currentSchema = isUber ? formSchemaUber : formSchema;

  const formMethods = useForm({
    mode: 'all',
    defaultValues: initialDataModel,
    resolver: yupResolver(currentSchema),
  });


  const handleSubmit = (values) => {
    setLoading(true);
    const dataObj = {
      readonly: Boolean(mode === PROVIDER_MODE.READONLY),
      provider: providerSRV,
      businessUuid: currentBusiness?.uuid || '',
      data: {
        username: values.username,
        password: values.password,
        previewUrl: values.previewUrl,
        storeId: values.storeId,
      },
    };
    delete values?.generalAccountEmail;
    return dispatch(connectStoreProvider(dataObj))
      .then(() => {
        dispatch(getProvidersStatuses());
      })
      .catch(() => setLoading(true))
      .finally(() => {
        setLoading(true);
        handleClose();
      });
  };

  const isProviderHasSteps = useMemo(() => {
    return providers?.grubhubv2?.srv === providerSRV;
  }, [providers, providerSRV]);

  
  useEffect(() => {
    if (filteredListWithGeneralAccountEmail?.generalAccountEmail && isUber) {
      formMethods.setValue('generalAccountEmail',
         filteredListWithGeneralAccountEmail.generalAccountEmail || '');
    }
  }, [filteredListWithGeneralAccountEmail, isUber]);

  useEffect(() => {
    if (!open) {
      formMethods.clearErrors('generalAccountEmail');
      formMethods.reset({
        generalAccountEmail: '',
      });
    }
  }, [open]);

  const handleBack = () => {
    setProviderReadOnlyStep(PROVIDER_MODAL_STEP.READONLY);
    formMethods.reset(initialDataModel);
  };

  const hasDisputeOnlyOption = useMemo(() => {
    return (providers?.ubereats?.srv === providerSRV) ||
       (providers?.doordash?.srv === providerSRV);
  },[providers, providerSRV]);

  const handleSave = () => {
    if (isUber) {
      localStorage.setItem('generalAccountEmail', formMethods.getValues('generalAccountEmail'));
      handleContinue();
      return;
    }
    isProviderHasSteps ? 
    setProviderReadOnlyStep(PROVIDER_MODAL_STEP.CONNECT) : handleContinue();
  };


  return {
    formMethods,
    handleSubmit,
    loading,
    isProviderHasSteps,
    isUber,
    hasDisputeOnlyOption,
    handleBack,
    handleSave,
  };
};

export default useProviderConnectReadOnly;
